/**
 * 路单计算工具 (百家乐/龙虎斗)
 *
 * 功能:
 * - 珠路计算
 * - 大路计算
 * - 大眼路计算
 * - 小路计算
 * - 蟑螂路(小强路)计算
 * - 统计数据计算
 *
 */

/**
 * 游戏类型
 */
export type GameType = 'BJL' | 'LHD'

/**
 * 原始游戏数据项
 *
 * 数据格式:
 * - 百家乐: (庄1、闲2、和3) + 分隔符(/) + 对子(庄对4、闲对5、庄闲对6)
 *   示例: '1/4' = 庄赢+庄对, '2/5' = 闲赢+闲对, '3' = 和局, '1/6' = 庄赢+庄闲对
 *
 * - 龙虎: (龙6、虎7、和8)
 *   示例: '6' = 龙赢, '7' = 虎赢, '8' = 和局
 */
export type RawGameData = string

/**
 * 解析后的游戏数据
 */
export interface ParsedGameData {
  /** 游戏类型 */
  type: GameType
  /** 主赢结果: 1=庄/龙, 2=闲/虎, 3=和 */
  main: number
  /** 庄对 (仅百家乐) */
  bankerPair?: boolean
  /** 闲对 (仅百家乐) */
  playerPair?: boolean
  /** 和的次数 */
  tieCount: number
  /** 原始数据 */
  rawData: string
  /** 是否为连赢平移数据 */
  isStreaking: boolean
}

/**
 * 路单结果数据 (用于 Canvas 渲染)
 */
export interface RoadResult {
  /** 主赢: 1=庄/龙, 2=闲/虎, 3=和 (衍生路只有 1,2) */
  main: number
  /** 庄对 (仅百家乐) */
  bankerPair?: boolean
  /** 闲对 (仅百家乐) */
  playerPair?: boolean
  /** 和的次数 */
  tieCount?: number
}

/**
 * 完整的路单数据
 *
 * 注意: 路单数组中可能包含 null 值 (表示空位)
 */
export interface RoadmapData {
  /** 珠路 */
  zhulu: (RoadResult | null)[][]
  /** 大路 */
  mainRoad: (RoadResult | null)[][]
  /** 大眼路 */
  bigEye: (RoadResult | null)[][]
  /** 小路 */
  smallEye: (RoadResult | null)[][]
  /** 蟑螂路 */
  cockroach: (RoadResult | null)[][]
}

/**
 * 百家乐统计数据
 */
export interface BaccaratStats {
  /** 总局数 */
  total: number
  /** 庄赢次数 */
  banker: number
  /** 闲赢次数 */
  player: number
  /** 和次数 */
  tie: number
  /** 庄对次数 */
  bankerPair: number
  /** 闲对次数 */
  playerPair: number
}

/**
 * 龙虎斗统计数据
 */
export interface DragonTigerStats {
  /** 总局数 */
  total: number
  /** 龙赢次数 */
  dragon: number
  /** 虎赢次数 */
  tiger: number
  /** 和次数 */
  tie: number
}

/**
 * 路单位置信息 (内部使用)
 */
interface RoadPosition {
  /** 当前列下标 */
  col: number
  /** 当前行下标 */
  row: number
  /** 上一个列下标 */
  lastCol: number
  /** 上一个行下标 */
  lastRow: number
  /** 上一个主赢 */
  lastMain: number
  /** 连赢列下标 */
  winCol: number
  /** 连赢行下标 */
  winRow: number
}

/**
 * 解析原始游戏数据
 *
 * @param gameData - 原始游戏数据数组 (如: ['1', '2/5', '6', '7', '3'])
 * @param gameType - 游戏类型
 * @returns 解析后的游戏数据
 */
export function handleListString(gameData: RawGameData[], gameType: GameType): ParsedGameData[] {
  if (gameType === 'BJL') {
    // 百家乐数据解析
    const results: ParsedGameData[] = []

    gameData.forEach(item => {
      if (!item) return

      const parts = item.split('/')

      // 主赢结果: 1=庄, 2=闲, 3=和
      const mainPart = parts.find(p => ['1', '2', '3'].includes(p))
      if (!mainPart) return // 跳过无效数据

      const mainMap: Record<string, number> = { '1': 1, '2': 2, '3': 3 }
      const mainResult = mainMap[mainPart]! // 已经通过 find 验证,不会是 undefined

      results.push({
        type: 'BJL',
        main: mainResult,
        bankerPair: parts.includes('4') || parts.includes('6'),
        playerPair: parts.includes('5') || parts.includes('6'),
        tieCount: 0,
        rawData: item,
        isStreaking: false,
      })
    })

    return results
  } else if (gameType === 'LHD') {
    // 龙虎数据解析
    const results: ParsedGameData[] = []

    gameData.forEach(item => {
      if (!item) return

      const parts = item.split('/')

      // 主赢结果: 6=龙, 7=虎, 8=和
      const mainValue = parts.find(p => ['6', '7', '8'].includes(p))
      if (!mainValue) return // 跳过无效数据

      const mainMap: Record<string, number> = { '6': 1, '7': 2, '8': 3 }
      const mainResult = mainMap[mainValue]! // 已经通过 find 验证,不会是 undefined

      results.push({
        type: 'LHD',
        main: mainResult,
        tieCount: 0,
        rawData: item,
        isStreaking: false,
      })
    })

    return results
  }

  // 未知类型返回空数组
  return []
}

/**
 * 计算统计数据
 *
 * @param parsedList - 解析后的游戏数据
 * @param gameType - 游戏类型
 * @returns 统计数据
 */
export function calculateWinCounts(parsedList: ParsedGameData[], gameType: GameType): BaccaratStats | DragonTigerStats {
  if (gameType === 'BJL') {
    const stats: BaccaratStats = {
      total: 0,
      banker: 0,
      player: 0,
      tie: 0,
      bankerPair: 0,
      playerPair: 0,
    }

    parsedList.forEach(item => {
      if (!item) return

      // 统计总局数 (有效数据)
      stats.total++

      if (item.main === 1) stats.banker++
      else if (item.main === 2) stats.player++
      else if (item.main === 3) stats.tie++

      if (item.bankerPair) stats.bankerPair++
      if (item.playerPair) stats.playerPair++
    })

    return stats
  } else if (gameType === 'LHD') {
    const stats: DragonTigerStats = {
      total: 0,
      dragon: 0,
      tiger: 0,
      tie: 0,
    }

    parsedList.forEach(item => {
      if (!item) return

      // 统计总局数 (有效数据)
      stats.total++

      if (item.main === 1) stats.dragon++
      else if (item.main === 2) stats.tiger++
      else if (item.main === 3) stats.tie++
    })

    return stats
  }

  // 未知类型返回空对象
  return {} as never
}

/**
 * 将 ParsedGameData 转换为 RoadResult (用于 Canvas 渲染)
 */
function toRoadResult(item: ParsedGameData): RoadResult {
  const result: RoadResult = {
    main: item.main,
  }

  // 添加对子信息 (仅百家乐)
  if (item.type === 'BJL') {
    if (item.bankerPair) result.bankerPair = true
    if (item.playerPair) result.playerPair = true
  }

  // 添加和的次数
  if (item.tieCount > 0) {
    result.tieCount = item.tieCount
  }

  return result
}

/**
 * 生成珠路数据
 *
 * @param parsedData - 解析后的游戏数据
 * @returns 珠路数据 (按列组织的二维数组,每列6行)
 */
export function generateBeadRoad(parsedData: ParsedGameData[]): RoadResult[][] {
  const beadRoad: RoadResult[][] = []
  let currentCol: RoadResult[] = []

  parsedData.forEach(item => {
    currentCol.push(toRoadResult(item))
    if (currentCol.length === 6) {
      beadRoad.push(currentCol)
      currentCol = []
    }
  })

  // 如果还有剩余数据,添加最后一列
  if (currentCol.length > 0) {
    beadRoad.push(currentCol)
  }

  return beadRoad
}

/**
 * 计算所有路方法显示(大路)
 *
 * @param gameList - 解析后的游戏数据
 * @returns 大路数据 (按列组织的二维数组)
 */
export function calculateRoad(gameList: ParsedGameData[]): (ParsedGameData | null)[][] {
  if (gameList.length === 0) return []

  const roadList: (ParsedGameData | null)[][] = []
  const position: RoadPosition = {
    col: 0,
    row: 0,
    lastCol: 0,
    lastRow: 0,
    lastMain: 0,
    winCol: 0,
    winRow: 0,
  }

  gameList.forEach(item => {
    // 和赢
    if (item.main === 3) {
      // 处理和赢：在当前列累加 tieCount，或在不存在时初始化
      if (!roadList[position.lastCol]) {
        roadList[position.lastCol] = Array(6).fill(null)
      }
      const column = roadList[position.lastCol]
      if (!column) return

      if (!column[position.lastRow]) {
        // 如果历史数据第一个是和，就初始化一个 item, tieCount=1
        column[position.lastRow] = { ...item, tieCount: 1 }
      } else {
        // 累加在已存在庄/闲的 tieCount里面
        const existingItem = column[position.lastRow]
        if (existingItem) {
          existingItem.tieCount++
        }
      }
      return
    }

    // 如果当前主赢和上一个主赢相同,则进行连赢处理
    if (item.main === position.lastMain) {
      // 连赢处理
      if (!roadList[position.col]) {
        // 无论数据是如何,都是不可能进入到这个地方,防止意外出现
        roadList[position.col] = Array(6).fill(null)
        position.winCol = position.winCol === 0 ? position.col + 1 : position.winCol
        position.winRow = 5
        roadList[position.winCol] = Array(6).fill(null)
        const winCol = roadList[position.winCol]
        if (winCol) {
          winCol[position.winRow] = item
        }
        position.lastCol = position.winCol
        position.lastRow = position.winRow
        position.winCol++
      } else {
        // 查找当前列表中第一个 null 的下标
        const currentCol = roadList[position.col]
        if (!currentCol) return
        const firstNullIndex = currentCol.findIndex(i => i === null)
        // 查找当前列表中第一个 isStreaking 为 true 的值
        const isStreakingIndex = currentCol.findIndex(item => item?.isStreaking === true)

        if (firstNullIndex !== -1) {
          if (firstNullIndex > isStreakingIndex && isStreakingIndex !== -1) {
            position.winCol = position.winCol === 0 ? position.col + 1 : position.winCol
            const isItem = roadList[position.winCol]?.findIndex(i => i !== null)

            // 如果当前列没有 null,则初始化一列
            if (isItem === -1 || isItem === undefined) {
              roadList[position.winCol] = Array(6).fill(null)
            }
            position.winRow = position.row === 0 ? 5 : position.lastRow
            item.isStreaking = true
            const winCol = roadList[position.winCol]
            if (winCol) {
              winCol[position.winRow] = item
            }
            position.lastCol = position.winCol
            position.lastRow = position.winRow
            // 如果当前列没有 null,添加了新列需要平移一列
            if (isItem === -1 || isItem === undefined || isItem > position.lastRow) {
              position.winCol++
            }
          } else {
            const col = roadList[position.col]
            if (col) {
              col[firstNullIndex] = item
            }
            position.lastCol = position.col
            position.lastRow = firstNullIndex
            position.row = firstNullIndex + 1
          }
        } else {
          // 如果当前列没有 null，则在新列最后一行加
          position.winCol = position.winCol === 0 ? position.col + 1 : position.winCol
          if (!roadList[position.winCol]) {
            roadList[position.winCol] = Array(6).fill(null)
            position.winRow = position.row === 0 ? 5 : position.lastRow
            item.isStreaking = true
          } else {
            // 查找当前列表中第一个null
            if (isStreakingIndex !== -1) {
              // 获取最新一列是否有连赢情况
              position.winRow = position.winRow === 0 ? position.lastRow : position.winRow
              item.isStreaking = true
            } else {
              // 倒序获取第一个 null 的下标
              const winColData = roadList[position.winCol]
              if (winColData) {
                const reversedArray = winColData.slice().reverse()
                const firstNullIndexEnd = reversedArray.findIndex(item => item === null)
                const originalIndex = reversedArray.length - firstNullIndexEnd - 1
                position.winRow = position.winRow === 0 ? originalIndex : position.winRow
              }
            }
          }
          const winCol = roadList[position.winCol]
          if (winCol) {
            winCol[position.winRow] = item
          }
          position.lastCol = position.winCol
          position.lastRow = position.winRow
          position.winCol++
        }
      }
    } else {
      // 处理与上一个主赢不同的情况 不是连赢的情况
      // 当前值与上一个值不同，则在新一列添加
      position.col++

      // 如果当前列不存在，则初始化一列
      if (!roadList[position.col]) {
        roadList[position.col] = Array(6).fill(null)
        position.row = 0
        const newCol = roadList[position.col]
        if (newCol) {
          newCol[position.row] = item
        }
        position.lastCol = position.col
        position.lastRow = position.row
        position.row++
      } else {
        // 如果当前列存在，则查找当前列第一个null
        const existingCol = roadList[position.col]
        if (!existingCol) return
        const firstNullIndex = existingCol.findIndex(item => item === null)
        const firstStreakingIndex = existingCol.findIndex(item => item?.isStreaking === true)
        if (firstNullIndex !== -1 && firstNullIndex < firstStreakingIndex) {
          existingCol[firstNullIndex] = item
          position.lastCol = position.col
          position.lastRow = firstNullIndex
          position.row = firstNullIndex + 1
        } else {
          // 如果当前列没有null，则在新列最后一行加
          position.row = 0
          const streakingCol = isStreaking(roadList, position.col)
          if (streakingCol !== false) {
            position.col = streakingCol
          }
          if (!roadList[position.col]) {
            roadList[position.col] = Array(6).fill(null)
          }
          const currentCol = roadList[position.col]
          if (currentCol) {
            currentCol[position.row] = item
          }
          position.lastCol = position.col
          position.lastRow = position.row
          position.row++
        }
      }

      // 重置连赢信息
      position.winCol = 0
      position.winRow = 0
    }

    // 更新上一个主赢
    position.lastMain = item.main
  })

  return roadList
}

/**
 * 递归判断是否连赢路
 */
function isStreaking(list: (ParsedGameData | null)[][], index: number): number | false {
  if (!list[index]) return index
  const firstNullIndex = list[index]?.findIndex(item => item === null)
  const streakingIndex = list[index]?.findIndex(item => item?.isStreaking === true)
  if (
    firstNullIndex !== undefined &&
    firstNullIndex !== -1 &&
    streakingIndex !== undefined &&
    streakingIndex > firstNullIndex
  ) {
    return index
  }
  index++
  return isStreaking(list, index)
}

/**
 * 生成大路数据
 *
 * @param parsedData - 解析后的游戏数据
 * @returns 大路数据 (RoadResult 格式,可能包含 null)
 */
export function generateBigRoad(parsedData: ParsedGameData[]): (RoadResult | null)[][] {
  const roadList = calculateRoad(parsedData)
  // 保持 null 值,不转换成空对象
  return roadList.map(column => column.map(item => (item ? toRoadResult(item) : null)))
}

/**
 * 创建不会转弯的二维列表,然后计算出大眼路、小路、小强路的一维列表
 *
 * @param gameList - 解析后的游戏数据
 * @returns 包含三个衍生路一维列表的对象
 */
export function createNoTurnRoadList(gameList: ParsedGameData[]): {
  bigEyeRoadList: ParsedGameData[]
  smallEyeRoadList: ParsedGameData[]
  smallCockroachRoadList: ParsedGameData[]
} {
  const roadList: ParsedGameData[][] = []
  const bigEyeRoadList: ParsedGameData[] = []
  const smallEyeRoadList: ParsedGameData[] = []
  const smallCockroachRoadList: ParsedGameData[] = []

  gameList.forEach(item => {
    // 如果是和，直接跳过
    if (item.main === 3) return

    const lastColumn = roadList[roadList.length - 1]
    const lastItem = lastColumn?.[lastColumn.length - 1]
    if (!lastColumn || !lastItem || lastItem.main !== item.main) {
      // 如果没有列 或者 与上一项不一样，创建新列
      roadList.push([item])
    } else {
      // 如果一样，就添加到当前列
      lastColumn.push(item)
    }

    // 大眼路
    const bigEyeRoad = calOtherRoad(roadList, 1)
    if (bigEyeRoad) bigEyeRoadList.push(bigEyeRoad)

    // 小路
    const smallEyeRoad = calOtherRoad(roadList, 2)
    if (smallEyeRoad) smallEyeRoadList.push(smallEyeRoad)

    // 蟑螂路
    const smallCockroachRoad = calOtherRoad(roadList, 3)
    if (smallCockroachRoad) smallCockroachRoadList.push(smallCockroachRoad)
  })

  return {
    bigEyeRoadList,
    smallEyeRoadList,
    smallCockroachRoadList,
  }
}

/**
 * 计算大眼路,小路,小强路
 *
 * 参考: calOtherRoad (JS 版本)
 *
 * @param gameList - 大路列表
 * @param startIndex - 起始下标 (1=大眼路, 2=小路, 3=蟑螂路)
 * @returns 衍生路结果
 */
export function calOtherRoad(gameList: ParsedGameData[][], startIndex: number): ParsedGameData | null {
  if (gameList.length === 0) return null

  let lastItemIndex = gameList.length - 1 // 最后一列的下标
  const lastColumn = gameList[lastItemIndex]
  if (!lastColumn) return null

  let lastItemIndexLength = lastColumn.length // 最后一列的长度

  if (lastItemIndex >= startIndex) {
    if (lastItemIndexLength === 1) {
      lastItemIndex -= 1
    }

    if (lastItemIndex >= startIndex) {
      const firstList = gameList[lastItemIndex - startIndex]
      const currentList = gameList[lastItemIndex]

      if (!firstList || !currentList) return null

      const firstListLength = firstList.length
      let lastListLength = currentList.length
      if (lastItemIndexLength === 1) {
        lastListLength += 1
      }

      // 根据长度的比较，决定添加的颜色
      if (firstListLength >= lastListLength) {
        return { main: lastItemIndexLength === 1 ? 2 : 1 } as ParsedGameData
      } else if (firstListLength + 1 === lastListLength) {
        return { main: lastItemIndexLength === 1 ? 1 : 2 } as ParsedGameData
      } else if (firstListLength + 1 < lastListLength) {
        return { main: lastItemIndexLength === 1 ? 2 : 1 } as ParsedGameData
      }
    }
  }

  return null
}

/**
 * 生成大眼路数据
 *
 * 参考: generateBigEyeRoad (JS 版本)
 *
 * @param bigEyeRoadList - 大眼路一维列表 (从 createNoTurnRoadList 获取)
 * @returns 大眼路数据 (RoadResult 格式,可能包含 null)
 */
export function generateBigEyeRoad(bigEyeRoadList: ParsedGameData[]): (RoadResult | null)[][] {
  return generateBigRoad(bigEyeRoadList)
}

/**
 * 生成小路数据
 *
 * 参考: generateSmallRoad (JS 版本)
 *
 * @param smallEyeRoadList - 小路一维列表 (从 createNoTurnRoadList 获取)
 * @returns 小路数据 (RoadResult 格式,可能包含 null)
 */
export function generateSmallRoad(smallEyeRoadList: ParsedGameData[]): (RoadResult | null)[][] {
  return generateBigRoad(smallEyeRoadList)
}

/**
 * 生成蟑螂路数据
 *
 * 参考: generateCockroachRoad (JS 版本)
 *
 * @param smallCockroachRoadList - 蟑螂路一维列表 (从 createNoTurnRoadList 获取)
 * @returns 蟑螂路数据 (RoadResult 格式,可能包含 null)
 */
export function generateCockroachRoad(smallCockroachRoadList: ParsedGameData[]): (RoadResult | null)[][] {
  return generateBigRoad(smallCockroachRoadList)
}

/**
 * 生成完整的路单数据 (一次性计算所有路)
 *
 * 这是对外的主入口函数,按照 JS 版本的调用方式实现:
 * 1. handleListString - 解析原始数据
 * 2. generateBeadRoad - 生成珠路
 * 3. generateBigRoad - 生成大路
 * 4. createNoTurnRoadList - 生成衍生路一维列表
 * 5. generateBigEyeRoad/generateSmallRoad/generateCockroachRoad - 转换为二维格式
 *
 * @param rawData - 原始游戏数据
 * @param gameType - 游戏类型
 * @returns 完整的路单数据
 */
export function generateRoadmap(rawData: RawGameData[], gameType: GameType): RoadmapData {
  // 1. 解析原始数据 (对应 JS 版本的 handleListString)
  const parsedData = handleListString(rawData, gameType)

  // 2. 生成珠路和大路
  const zhulu = generateBeadRoad(parsedData)
  const mainRoad = generateBigRoad(parsedData)

  // 3. 一次性生成大眼路、小路、蟑螂路的一维列表 (对应 JS 版本的 createNoTurnRoadList)
  const { bigEyeRoadList, smallEyeRoadList, smallCockroachRoadList } = createNoTurnRoadList(parsedData)

  // 4. 将一维列表转换为二维路单格式
  const bigEye = generateBigEyeRoad(bigEyeRoadList)
  const smallEye = generateSmallRoad(smallEyeRoadList)
  const cockroach = generateCockroachRoad(smallCockroachRoadList)

  return {
    zhulu,
    mainRoad,
    bigEye,
    smallEye,
    cockroach,
  }
}
